import SearchIcon from "@mui/icons-material/Search";
import { Box, Grid, styled, Tab, TextField, Stack, Chip } from "@mui/material";
import { api, types } from "api";
import { ColorTabs } from "components/ColorTabs";
import clsx from "clsx";
import { green } from "@mui/material/colors";
import FormControl from "@mui/material/FormControl";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { routes } from "routes";
import { useAuthStatus } from "../../helpers/useAuthStatus";
import { GET_RESULT_FAIL } from "../../messages";
import { RegisteredUserTable } from "./Component/RegisteredUserTable";

import { Paper, Typography } from "@mui/material";

const PREFIX = "RegisteredUserTablePage";

const classes = {
	root: `${PREFIX}-root`,
	admin: `${PREFIX}-admin`,
	teacher: `${PREFIX}-teacher`,
	student: `${PREFIX}-student`,
	parent: `${PREFIX}-parent`,

	button: `${PREFIX}-button`,
	buttonList: `${PREFIX}-buttonList`,

	appBar: `${PREFIX}-appBar`,
	indicator: `${PREFIX}-indicator`,
	table: `${PREFIX}-table`,
	tabs: `${PREFIX}-tabs`,
	tabsBarBox: `${PREFIX}-tabsBarBox`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		flexGrow: 1,
	},
	[`& .${classes.tabs}`]: {
		color: "white",
	},
	[`& .${classes.button}`]: {
		minWidth: theme.spacing(21),
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	[`& .${classes.tabsBarBox}`]: {
		backgroundColor: theme.palette.primary.main,
	},
	[`& .${classes.buttonList}`]: {
		display: "flex",
	},

	[`& .${classes.appBar}`]: {
		backgroundColor: theme.palette.primary.main,
	},

	[`& .${classes.table}`]: {
		marginTop: theme.spacing(4),
	},
}));

const CustomChip = styled("div")(({ theme }) => ({
	[`& .${classes.root}`]: {
		backgroundColor: "#d32f2f",
		color: theme.palette.background.paper,
		marginRight: theme.spacing(1),
	},
	[`& .${classes.admin}`]: {
		backgroundColor: theme.palette.secondary.main,
		color: theme.palette.background.paper,
		marginRight: theme.spacing(1),
	},
	[`& .${classes.teacher}`]: {
		backgroundColor: theme.palette.secondary.light,
		color: theme.palette.background.paper,
		marginRight: theme.spacing(1),
	},
	[`& .${classes.student}`]: {
		backgroundColor: green[400],
		color: theme.palette.background.paper,
		marginRight: theme.spacing(1),
	},
	[`& .${classes.parent}`]: {
		backgroundColor: theme.palette.secondary.dark,
		color: theme.palette.background.paper,
		marginRight: theme.spacing(1),
	},
}));

export const RegisteredUserTablePage = (): JSX.Element => {
	const { role } = useAuthStatus();
	const [, setErr] = useState("");
	const [searchUsername, setSearchUsername] = useState("");
	const [registeredUsers, setRegisteredUsers] =
		useState<types.RegisteredUserList>([]);
	const [allRegisteredUsers, setAllRegisteredUsers] =
		useState<types.RegisteredUserList>([]);
	const [isloading, setIsloading] = useState(true);
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");
	const fetchRegisteredUser = useCallback(() => {
		api
			.getRegisteredUsers(fromDate, toDate)
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr("");
				if (res.data === null || res.data.length === 0) {
					setRegisteredUsers([]);
					return;
				}
				setRegisteredUsers(res.data);
				setAllRegisteredUsers(res.data);
				setIsloading(false);
			})
			.catch(() => {
				setErr(GET_RESULT_FAIL);
			});
	}, [fromDate, toDate]);

	useEffect(() => {
		fetchRegisteredUser();
	}, [role, fetchRegisteredUser]);

	return (
		<Root className={classes.root}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				className={classes.tabsBarBox}
			>
				<ColorTabs
					value={routes._.metrics._.RegisteredUsers.path}
					variant="scrollable"
					scrollButtons
				>
					<Tab
						label="GROUP LIST"
						value={routes._.metrics._.Groups.path}
						sx={{ color: "white" }}
						classes={{
							root: classes.tabs,
						}}
						component={Link}
						to={routes._.metrics._.Groups.path}
					/>
					<Tab
						label="STUDENT LIST"
						value={routes._.metrics._.Students.path}
						sx={{ color: "white" }}
						classes={{
							root: classes.tabs,
						}}
						component={Link}
						to={routes._.metrics._.Students.path}
					/>
					<Tab
						label="School Metrics"
						value={routes._.metrics._.School.path}
						sx={{ color: "white" }}
						classes={{
							root: classes.tabs,
						}}
						component={Link}
						to={routes._.metrics._.School.path}
					/>
					{role === "admin" && (
						<Tab
							label="Registered Users"
							value={routes._.metrics._.RegisteredUsers.path}
							sx={{ color: "white" }}
							classes={{
								root: classes.tabs,
							}}
							component={Link}
							to={routes._.metrics._.RegisteredUsers.path}
						/>
					)}
				</ColorTabs>
			</Box>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography variant="h3" data-testid="hello">
						Hi Admin
					</Typography>
					<Typography variant="h6" data-testid="hello">
						Welcome back to Science Island. Here&quot; a summary of what
						you&quot;ve missed
					</Typography>
				</Grid>

				<Grid item xs={10}>
					<RegisteredUserTable users={registeredUsers} isloading={isloading}>
						<Grid item xs={4}>
							<TextField
								id="group-name"
								label="Admin Name"
								variant="outlined"
								value={searchUsername}
								onChange={(e) => {
									const searchName = e.target.value;
									if (searchName.length < searchUsername.length) {
										// monitor backspace
										setRegisteredUsers(allRegisteredUsers);
									} else {
										let searchUsers: types.RegisteredUserList = [];
										const regex = /^ *$/;
										// a name is null or empty string or all whitespace
										if (regex.exec(searchName) !== null) {
											setRegisteredUsers(allRegisteredUsers);
										} else {
											registeredUsers.forEach((user) => {
												const name = user.firstName + " " + user.lastName;
												if (
													name
														.toLowerCase()
														.includes(searchName.trim().toLowerCase())
												) {
													searchUsers = [...searchUsers, user];
												}
											});
											setRegisteredUsers(searchUsers);
										}
									}
									setSearchUsername(searchName);
								}}
								fullWidth
								InputProps={{
									endAdornment: <SearchIcon />,
								}}
							/>
						</Grid>
					</RegisteredUserTable>
				</Grid>

				<Grid item xs={2}>
					<Paper sx={{ width: "100%", mb: 2 }}>
						<FormControl sx={{ m: 1, minWidth: 120 }}>
							<Typography variant="h4" data-testid="hello">
								Filter
							</Typography>
							<div style={{ marginBottom: 4, marginTop: 4 }}>Date From</div>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<Stack spacing={2}>
									<DatePicker
										value={fromDate}
										inputFormat="YYYY-MM-DD"
										onChange={(date: any) => {
											const dateString = new Date(date)
												.toISOString()
												.slice(0, 10);
											setFromDate(dateString);
										}}
										renderInput={(params) => <TextField {...params} />}
									/>
									<div>To</div>
									<DatePicker
										inputFormat="YYYY-MM-DD"
										value={toDate}
										onChange={(date: any) => {
											const dateString = new Date(date)
												.toISOString()
												.slice(0, 10);
											setToDate(dateString);
										}}
										renderInput={(params) => <TextField {...params} />}
									/>
								</Stack>
							</LocalizationProvider>

							<div style={{ marginBottom: 10, marginTop: 10 }}>Roles</div>
							<CustomChip>
								<Grid container spacing={2}>
									<Grid item xs={5}>
										<Chip
											className={clsx({
												[classes.admin]: true,
											})}
											label={"admin"}
										/>
									</Grid>
									<Grid item xs={5}>
										<Chip
											className={clsx({
												[classes.student]: true,
											})}
											label={"student"}
										/>
									</Grid>
									<Grid item xs={6}>
										<Chip
											className={clsx({
												[classes.teacher]: true,
											})}
											label={"teacher"}
										/>
									</Grid>
									<Grid item xs={6}>
										<Chip
											className={clsx({
												[classes.admin]: true,
											})}
											label={"admin"}
										/>
									</Grid>
								</Grid>
							</CustomChip>
						</FormControl>
					</Paper>
				</Grid>
			</Grid>
		</Root>
	);
};
